import React, { useState, useEffect } from "react";
import "./StudentsHeader.css";
import { useSelector } from "react-redux";
import {
  createPermissionChecker,
  PERMISSION_TYPES,
} from "../../shared/utils/permissions";
import UploadButton from "../../shared/components/UploadButton/UploadButton";
import useEventsQueries from "../../store/events/eventsQueries";

const capitalizeFirstLetter = (str) => {
  if (!str || typeof str !== "string") return "";
  return str
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const StudentsHeader = ({
  schoolId,
  handleSchoolIdChange,
  handleGetData,
  isLoading,
  activeTab,
  children,
  schoolDetails,
  isUserAuthorized,
}) => {
  const state = useSelector((state) => state);
  const checkUserPermission = createPermissionChecker(state);
  const { handleSearchSchools } = useEventsQueries();
  const canUploadToSchool = useSelector(
    (state) => state.uploadPermissions.canUploadToSchool
  );

  const [searchInput, setSearchInput] = useState("");
  const [schoolOptions, setSchoolOptions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isActiveSearch, setIsActiveSearch] = useState(false);
  const [showCopyNotification, setShowCopyNotification] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".school-search-container")) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchInput && searchInput.length >= 3) {
        fetchSchoolOptions(searchInput);
        setShowDropdown(true);
      } else {
        setSchoolOptions([]);
        setShowDropdown(false);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchInput]);

  const fetchSchoolOptions = async (searchedSchool) => {
    setIsSearching(true);
    const response = await handleSearchSchools(searchedSchool);
    if (response) {
      const formattedSchools = response.data.map((school) => ({
        label: `${school._source.school_name} - (${school._source.school_id})`,
        value: school._source.school_id,
      }));
      setSchoolOptions(formattedSchools);
    } else {
      setSchoolOptions([]);
    }
    setIsSearching(false);
  };

  const handleSchoolSelect = (school) => {
    setIsActiveSearch(false);
    setSearchInput(school.value);
    handleSchoolIdChange({ target: { value: school.value } });
    setShowDropdown(false);
  };

  const canUploadStudents =
    checkUserPermission(PERMISSION_TYPES.UPLOAD_STUDENTS) && canUploadToSchool;

  useEffect(() => {
    if (!isActiveSearch && searchInput) {
      handleGetData();
    }
  }, [isActiveSearch, searchInput]);

  return (
    <>
      <div className="students-heading">
        <h3>Students</h3>
        <p>Manage your student data</p>
      </div>

      <div className="school-id-section">
        <span>Search School by ID or Name:</span>
        <div className="school-search-container">
          <input
            type="text"
            value={searchInput}
            onChange={(e) => {
              setIsActiveSearch(true);
              setSearchInput(e.target.value.toUpperCase());
            }}
            placeholder="Please enter a school ID"
            onFocus={() => {
              if (searchInput && searchInput.length >= 3) {
                setShowDropdown(true);
              }
            }}
          />
          {(showDropdown ||
            (searchInput &&
              searchInput.length > 0 &&
              searchInput.length < 3)) && (
            <div className="school-options-dropdown">
              {searchInput && searchInput.length < 3 ? (
                <div className="school-dropdown-message">
                  Type at least 3 characters to search
                </div>
              ) : isSearching ? (
                <div className="school-dropdown-message">Loading...</div>
              ) : schoolOptions.length > 0 ? (
                schoolOptions.map((school, index) => (
                  <div
                    key={index}
                    className="school-option-item"
                    onClick={() => handleSchoolSelect(school)}
                  >
                    {school.label}
                  </div>
                ))
              ) : (
                <div className="school-dropdown-message">No results found</div>
              )}
            </div>
          )}
        </div>
        <button
          className="get-data-btn"
          disabled={!searchInput || isLoading}
          onClick={() => {
            handleSchoolIdChange({ target: { value: searchInput } });
            handleGetData();
            setIsActiveSearch(false);
          }}
        >
          {isLoading ? "Loading..." : "Get Data"}
        </button>
        <div className="students-copy-button-container">
          {schoolDetails && schoolId && (
            <button
              className="students-copy-registration-link-btn"
              onClick={() => {
                navigator.clipboard.writeText(
                  `https://inmobiusinfinitylearn.com/?school_id=${schoolId}`
                );
                setShowCopyNotification(true);
                setTimeout(() => {
                  setShowCopyNotification(false);
                }, 2000);
              }}
            >
              Copy Registration Link
            </button>
          )}
          {showCopyNotification && (
            <div className="students-copy-notification">Link Copied</div>
          )}
        </div>
        {activeTab === "students" &&
          schoolId &&
          canUploadStudents &&
          isUserAuthorized && (
            <div className="upload-button-wrapper">
              <UploadButton
                schoolId={schoolId}
                uploadType="studentRegister"
                selectedTab="Onboarded Students"
              />
            </div>
          )}
      </div>
      {schoolDetails && (
        <div className="school-details-section">
          School:{" "}
          {[
            capitalizeFirstLetter(schoolDetails.school_name),
            capitalizeFirstLetter(schoolDetails.city),
            capitalizeFirstLetter(schoolDetails.state),
            capitalizeFirstLetter(schoolDetails.country),
            schoolDetails.pincode?.toString(),
          ]
            .filter(Boolean)
            .join(", ") +
            " | School ID: " +
            schoolDetails.school_id}
        </div>
      )}
    </>
  );
};

export default StudentsHeader;
